import { readSamhengiContext } from '@/lib/samhengiStorage';
import { useEffect, useRef, useState } from 'react';

export type VerificationSeverity = 'mild' | 'moderate' | 'severe';

export interface VerificationFinding {
  description: string;
  severity: VerificationSeverity;
  reason: string;
  /** Exact verbatim substring of the summary to replace. Empty when the defect is a pure omission. */
  original: string;
  /** Corrected replacement text, in the summary's language. */
  suggested_fix: string;
  /** Opaque identifier the backend assigns to this finding; never computed client-side. */
  finding_key: string;
}

export interface VerificationResult {
  status: 'ok' | 'error';
  total_real: number;
  severe: number;
  moderate: number;
  mild: number;
  findings: VerificationFinding[];
  verification_run_id?: string;
}

export type VerificationState = 'idle' | 'verifying' | 'done' | 'error';

interface UseSummaryVerificationArgs {
  sessionId: string | null;
  summary: string | null;
  transcription: string | null;
  enabled?: boolean; // default true; auto-runs when a summary is present
}

export const useSummaryVerification = ({
  sessionId,
  summary,
  transcription,
  enabled = true,
}: UseSummaryVerificationArgs) => {
  const [state, setState] = useState<VerificationState>('idle');
  const [result, setResult] = useState<VerificationResult | null>(null);
  // Track the complete input snapshot that last reached a response.
  const verifiedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !sessionId || !summary || !transcription) {
      // A run aborted by disabling must not leave the spinner up.
      setState((prev) => (prev === 'verifying' ? 'idle' : prev));
      return;
    }

    const verificationKey = JSON.stringify([sessionId, summary, transcription]);
    if (verifiedFor.current === verificationKey) return;

    const controller = new AbortController();
    setState('verifying');
    setResult(null);

    // Read the Samhengi context fresh at fetch time. SamhengiPanel writes
    // it to sessionStorage on textarea-blur and on a 300ms debounce, both of
    // which fire before any "Generate summary" click — so by the time the
    // verifier runs, the storage value is the user's latest context. Empty
    // string when absent means the backend skips the extra context block
    // and the prompts stay byte-identical to the no-context path.
    const preEncounterContext = readSamhengiContext(sessionId) || undefined;

    fetch('/api/summary/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        session_id: sessionId,
        audio_id: sessionId,
        summary,
        transcription,
        pre_encounter_context: preEncounterContext,
      }),
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: VerificationResult) => {
        if (controller.signal.aborted) return;
        verifiedFor.current = verificationKey;
        setResult(data);
        setState(data.status === 'ok' ? 'done' : 'error');
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setState('error');
      });

    return () => controller.abort();
  }, [enabled, sessionId, summary, transcription]);

  return { state, result };
};

export interface VerificationDecisionInput {
  finding_key: string;
  decision: 'accepted' | 'rejected';
  applied: boolean;
}

export const recordVerificationDecisions = (audioId: string, decisions: VerificationDecisionInput[]): void => {
  // Fire-and-forget: training-data persistence must never block or break the UI.
  void fetch('/api/summary/verify/decisions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ audio_id: audioId, decisions }),
  }).catch(() => {});
};
